import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import {
  fetchMicrogridForController,
  fetchMicrogridSharingControllers,
  createMicrogrid,
  updateMicrogrid,
  deleteMicrogrid,
} from './api'
import LoadingState from '@/components/common/LoadingState'
import ConfirmModal from '@/components/common/ConfirmModal'

/**
 * Add/edit/remove this ONE controller's microgrid link, embedded in
 * ControllerFormPage.tsx's edit view (/controllers/<id>/edit). A microgrid
 * links a device payload's microgrid_id to the controller_id/site it
 * belongs to -- see node_manifest_service.py and
 * resolve_microgrid_controller_id for why this matters: without a link
 * here, a genuine multi-node sub-device (device_id != controller_id) has
 * its telemetry silently skipped rather than attributed to this site.
 *
 * Full fleet-wide list/management also lives at /microgrids
 * (MicrogridsPage.tsx) -- same API, different vantage point (all sites at
 * once vs. one site in context).
 */
export default function MicrogridSection({ controllerId }: { controllerId: string }) {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('controllers.edit')
  const queryClient = useQueryClient()
  const queryKey = ['controller-microgrid', controllerId]

  const microgridQuery = useQuery({
    queryKey,
    queryFn: () => fetchMicrogridForController(controllerId),
    enabled: Boolean(controllerId),
  })

  const [microgridId, setMicrogridId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  useEffect(() => {
    setDisplayName(microgridQuery.data?.display_name ?? '')
  }, [microgridQuery.data])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: ['microgrids'] })
  }

  const createMutation = useMutation({
    mutationFn: () => createMicrogrid({ microgrid_id: microgridId, controller_id: controllerId, display_name: displayName }),
    onSuccess: () => {
      invalidate()
      setMicrogridId('')
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add to microgrid.'),
  })

  const updateMutation = useMutation({
    mutationFn: () => updateMicrogrid(microgridQuery.data!.microgrid_id, { display_name: displayName }),
    onSuccess: () => {
      invalidate()
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteMicrogrid(microgridQuery.data!.microgrid_id),
    onSuccess: () => {
      invalidate()
      setConfirmingRemove(false)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to remove.'),
  })

  const linked = microgridQuery.data

  // Every OTHER controller/device reporting the same microgrid_id, not yet
  // linked -- see fetchMicrogridSharingControllers for why this is a
  // separate question from "who's the registered owner" (a legacy
  // self-resolved controller's own telemetry always carries a real
  // microgrid_id even before it's ever formally linked).
  const sharingQuery = useQuery({
    queryKey: ['microgrid-sharing', linked?.microgrid_id],
    queryFn: () => fetchMicrogridSharingControllers(linked!.microgrid_id),
    enabled: Boolean(linked?.microgrid_id),
  })
  const otherSharingControllers = (sharingQuery.data?.controllers ?? []).filter(
    (c) => c.controller_id !== controllerId,
  )
  const unresolvedSharingDeviceIds = sharingQuery.data?.unresolved_device_ids ?? []

  if (microgridQuery.isLoading) return <LoadingState label="Loading microgrid…" />

  return (
    <div className="rounded-2xl border border-nav-line bg-card p-5">
      <h2 className="text-base font-semibold text-ink">Microgrid</h2>
      <p className="mt-1 text-xs text-text-secondary">
        Links this controller to a device payload&apos;s <code>microgrid_id</code>, so a multi-node device
        reporting under it (whose own <code>device_id</code> isn&apos;t independently registered) is attributed
        to this site instead of being skipped. See also the fleet-wide <a href="/microgrids" className="text-accent hover:underline">Microgrids</a> page.
      </p>

      {error && <p className="mt-2 text-sm text-error">{error}</p>}

      {linked ? (
        <div className="mt-3 space-y-2">
          <div className="text-sm text-text-primary">
            <span className="text-text-secondary">Microgrid ID:</span>{' '}
            <span className="font-mono text-xs">{linked.microgrid_id}</span>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Display Name</label>
            <div className="flex gap-2">
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={!canManage}
                className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary disabled:opacity-60 focus:border-brand focus:outline-none"
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                  className="shrink-0 rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              className="text-xs text-error hover:underline"
            >
              Remove from microgrid
            </button>
          )}
          {(otherSharingControllers.length > 0 || unresolvedSharingDeviceIds.length > 0) && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-text-primary">
              <p className="font-medium">
                {otherSharingControllers.length + unresolvedSharingDeviceIds.length} other device(s)/controller(s)
                also report microgrid_id {linked.microgrid_id} in the last 7 days:
              </p>
              <ul className="mt-1 space-y-1">
                {otherSharingControllers.map((c) => (
                  <li key={c.controller_id}>
                    <Link to={`/controllers/${c.controller_id}/edit`} className="text-accent hover:underline">
                      {c.controller_name || c.controller_id}
                    </Link>{' '}
                    <span className="font-mono text-text-secondary">({c.device_ids.join(', ')})</span>
                    {c.is_registered_owner && ' — registered owner'}
                  </li>
                ))}
                {unresolvedSharingDeviceIds.length > 0 && (
                  <li className="font-mono text-text-secondary">
                    Not attributable to any controller yet: {unresolvedSharingDeviceIds.join(', ')}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : canManage ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            createMutation.mutate()
          }}
          className="mt-3 space-y-2"
        >
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Microgrid ID</label>
            <input
              value={microgridId}
              onChange={(e) => setMicrogridId(e.target.value)}
              required
              placeholder="e.g. 25GMC41"
              className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Display Name (optional)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Adding…' : 'Add to Microgrid'}
          </button>
        </form>
      ) : (
        <p className="mt-3 text-sm text-text-secondary">Not part of a microgrid.</p>
      )}

      {linked && (
        <ConfirmModal
          isOpen={confirmingRemove}
          title="Remove from microgrid"
          message={`Remove this controller from microgrid ${linked.microgrid_id}? This does not delete any device's telemetry.`}
          confirmLabel="Remove"
          isConfirming={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
    </div>
  )
}
